import { throttle } from 'lodash';
import Button from '@mui/material/Button';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Empty from 'components/Empty';
import LoadingIndicator from 'components/LoadingIndicator';

const styles = {
  done: {
    marginTop: 20,
  },
  empty: {
    minHeight: '80vh',
    justifyContent: 'center',
    alignItems: 'center',
  },
};

const calculateTopPosition = el => (el ? el.offsetTop + calculateTopPosition(el.offsetParent) : 0);
const calculateOffset = el => calculateTopPosition(el)
  + (el.offsetHeight
    - (window ? window.pageYOffset : 0)
    - (window ? window.innerHeight : 0));

const InfiniteScroller = ({
  children,
  done,
  doneButtons,
  doneIcon,
  doneText,
  doneTitle = "That's everything!",
  empty,
  emptyIcon,
  emptyText,
  emptyTitle = "There's nothing here yet!",
  interval = 500,
  onEmptyClick,
  pageSize = 10,
  relay,
  showDone = true,
  showLoading = true,
  threshold = 500,
  varName = 'first',
  resize,
  scrollId = 'scrollContainer',
}) => {
  const [loading, setLoading] = useState(false);
  const [scrollContainer, setScrollContainer] = useState(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleScroll = useCallback(
    throttle(() => {
      if (scrollContainer && !empty) {
        if (done && window) {
          window.removeEventListener('scroll', handleScroll);
        } else if (!loading && calculateOffset(scrollContainer) <= threshold) {
          setLoading(true);
          relay.setVariables(
            { [varName]: Number(relay.variables[varName]) + Number(pageSize) },
            ({ ready, done: relayDone }) => {
              // eslint-disable-next-line no-unused-expressions
              ready && relayDone && setLoading(false);
              resize();
            },
          );
        }
      }
    }, interval),
    [empty, done, loading, scrollContainer],
  );
  useEffect(() => {
    setScrollContainer(document.getElementById(scrollId));
    if (window) {
      window.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (window) {
        window.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll, scrollId, setScrollContainer]);
  return empty ? (
    <Empty
      buttons={
        emptyText && (
          <Button disabled={!onEmptyClick} onClick={onEmptyClick} secondary>
            {emptyText}
          </Button>
        )
      }
      icon={emptyIcon}
      style={styles.empty}
      title={emptyTitle}
    />
  ) : (
    <Fragment>
      {children}
      {showLoading && loading && !done && <LoadingIndicator />}
      {showDone && !loading && done && (
        <Empty
          buttons={doneButtons}
          icon={doneIcon}
          style={styles.done}
          text={doneText}
          title={doneTitle}
        />
      )}
    </Fragment>
  );
};

InfiniteScroller.propTypes = {
  children: PropTypes.node,
  done: PropTypes.bool,
  doneButtons: PropTypes.node,
  doneIcon: PropTypes.node,
  doneText: PropTypes.string,
  doneTitle: PropTypes.string,
  empty: PropTypes.bool,
  emptyIcon: PropTypes.node,
  emptyText: PropTypes.string,
  emptyTitle: PropTypes.string,
  interval: PropTypes.number,
  onEmptyClick: PropTypes.func,
  pageSize: PropTypes.number,
  relay: PropTypes.object.isRequired,
  showDone: PropTypes.bool,
  showLoading: PropTypes.bool,
  threshold: PropTypes.number,
  varName: PropTypes.string,
  resize: PropTypes.func,
  scrollId: PropTypes.string,
};

export default InfiniteScroller;
